import { Link } from 'react-router-dom'
import { formatPrice, formatCountdown, formatDate, countryName } from '../utils/format.js'
import { STATUS_LABELS } from '../data/constants.js'
import SourceBadge from './SourceBadge.jsx'
import { originalUrl } from '../utils/originalUrl.js'

// Tarjeta de lote para listados.
// - Lotes ACTIVOS:  "Aktuelles Gebot"/"Startgebot", nº pujas, cuenta atrás, ubicación, casa,
//                    estado, botón "Zum Originalangebot".
// - Lotes TERMINADOS: "Letztes Gebot", fecha de fin, "Beendet", resultado del precio mínimo,
//                    botón "Originalangebot ansehen" (sin la palabra "bieten").
// Si no hay ninguna puja se muestra "Startgebot" (nunca "0 €"). (Requisito 4)
export default function LotCard({ lot }) {
  const image = lot.main_image_url || (lot.images && lot.images[0]?.image_url)
  const isEnded = lot.status === 'ended' || lot.status === 'sold' || lot.status === 'cancelled' || lot.status === 'reserve_not_met'
  const upcoming = lot.status === 'upcoming'
  const statusLabel = STATUS_LABELS[lot.status] || lot.status

  const origUrl = originalUrl(lot)

  // Vendedor/ciudad: país + ciudad en alemán
  const city = lot.city
  const country = countryName(lot.country_code)

  // Precio: puja actual o final según estado
  const hasBids = (lot.bids_count || 0) > 0
  const bidValue = lot.current_bid ?? lot.final_bid ?? lot.start_bid

  return (
    <article className="lot-card">
      <Link to={`/lote/${lot.id}`} className="lot-card-link" aria-label={lot.title}>
        <div className="lot-card-img">
          {image ? <img src={image} alt={lot.title} loading="lazy" /> : <span className="img-ph">🖼️</span>}
          {lot.status && <span className={`status-ribbon status-${String(lot.status).toLowerCase()}`}>{statusLabel}</span>}
        </div>

        <div className="lot-card-body">
          <h3 className="lot-card-title">{lot.title}</h3>

          {/* Bloque de precio - diferenciado por estado */}
          <div className="lot-card-meta">
            {isEnded ? (
              <>
                <span className="lot-label">Letztes Gebot</span>
                <span className="lot-price">{bidValue != null ? formatPrice(bidValue, lot.currency) : '–'}</span>
              </>
            ) : upcoming ? (
              <>
                <span className="lot-label">Startgebot</span>
                <span className="lot-price">{lot.start_bid != null ? formatPrice(lot.start_bid, lot.currency) : '–'}</span>
              </>
            ) : (
              <>
                <span className="lot-label">{hasBids ? 'Aktuelles Gebot' : 'Startgebot'}</span>
                <span className="lot-price">
                  {bidValue != null ? formatPrice(bidValue, lot.currency) : '–'}
                </span>
              </>
            )}
            {lot.bids_count != null && <span className="lot-bids">{lot.bids_count} Gebote</span>}
            {isEnded && !lot.bids_count && <span className="lot-bids">Keine Gebote</span>}
          </div>

          {/* Resultado del precio mínimo para terminados */}
          {isEnded && lot.reserve_status && (
            <div className="lot-reserve">{lot.reserve_status === 'met' ? 'Mindestpreis erreicht' : 'Mindestpreis nicht erreicht'}</div>
          )}

          <div className="lot-card-foot">
            {lot.closes_at ? (
              isEnded || upcoming ? (
                <span className="lot-close" title={`Endet: ${formatDate(lot.closes_at)}`}>
                  📅 {formatDate(lot.closes_at)}
                </span>
              ) : (
                <span className="lot-close" title={`Endet: ${formatDate(lot.closes_at)}`}>
                  ⏱ {formatCountdown(lot.closes_at)}
                </span>
              )
            ) : null}
            {city && <span className="lot-city">{city}</span>}
            {lot.country_code && <span className="lot-country">{country}</span>}
          </div>

          <div className="lot-card-tags">
            <SourceBadge source={lot.source} />
          </div>
        </div>
      </Link>

      {/* Enlace al anuncio original - texto distinto para activos vs terminados */}
      {origUrl && (
        <a
          className={`lot-original ${isEnded ? 'lot-original-ended' : ''}`}
          href={origUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {isEnded ? 'Originalangebot ansehen ↗' : 'Zum Originalangebot ↗'}
        </a>
      )}
    </article>
  )
}
